import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi, ApiError } from '../lib/api';
import type { Organization, User, UserRole } from '../lib/types';

interface AuthState {
  user: User | null;
  org: Organization | null;
  loading: boolean;
  demoLogin: (role: UserRole) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await authApi.me();
      setUser(res.user);
      setOrg(res.org);
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        // eslint-disable-next-line no-console
        console.error('Failed to load session', err);
      }
      setUser(null);
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const demoLogin = useCallback(
    async (role: UserRole) => {
      const res = await authApi.demoLogin(role);
      setUser(res.user);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setOrg(null);
  }, []);

  const value = useMemo(() => ({ user, org, loading, demoLogin, logout, refresh }), [user, org, loading, demoLogin, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
